import { useEffect, useMemo, useState } from 'react';
import { api } from '../../api/client';
import { AppDialog } from '../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { GridSearch } from '../../components/GridSearch';
import { useNotify } from '../../components/notifications';
import { useSaveFlow } from '../../components/confirm';
import { confirmDiscardIfUnsaved, useUnsavedGuard } from '../../hooks/useUnsavedGuard';
import { filterItems, sameSelection, type AssignItemRow } from './assignItemsModel';
import './AssignItemsDialog.css';

const FORM_NAME = 'Assign Items To Test';

// Legacy Items_ListView_Copy_TestMethodLink columns: Item · Description (the item's
// Specification) · Vendor · Catalog#.
const COLUMNS: AppColumn<AssignItemRow>[] = [
  { field: 'item', header: 'Item', width: 280 },
  { field: 'description', header: 'Description', width: 260 },
  { field: 'vendor', header: 'Vendor', width: 150 },
  { field: 'catalogNo', header: 'Catalog#' },
];

/**
 * Assign Items To Test — the item picker opened by double-clicking a test's Item
 * cell: every live Item with the test's items ticked; OK saves the ticked set.
 * Built from the shared AppDialog + GridSearch + AppDataGrid and saved through the
 * shared useSaveFlow ("Do you want to Save?" → save → "Saved successfully."), like
 * every other popup.
 */
export function AssignItemsDialog({ testMethodOid, testCode, testName, onClose, onSaved }: {
  testMethodOid: string;
  testCode: string;
  testName: string;
  onClose: () => void;
  /** After a successful save — close and refresh the list. */
  onSaved: () => void;
}) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const [rows, setRows] = useState<AssignItemRow[]>([]);
  const [linked, setLinked] = useState<ReadonlySet<string | number>>(new Set());
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get<{ data?: AssignItemRow[] }>(`/api/v1/inventory-items/test-method/${testMethodOid}/popup-items`)
      .then(({ data }) => {
        if (!alive) return;
        const list = data.data ?? [];
        const ids = new Set<string | number>(list.filter((r) => r.isLinked).map((r) => r.oid));
        setRows(list);
        setLinked(ids);
        setSelected(new Set(ids));
      })
      .catch(() => { if (alive) notify.error('Could not load the items.'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [testMethodOid, notify]);

  const visible = useMemo(() => filterItems(rows, search), [rows, search]);
  const dirty = !sameSelection(selected, linked);
  // The ticks live in grid checkboxes the automatic unsaved-changes tracker ignores,
  // so the popup declares its own state (standing rule 15).
  useUnsavedGuard(dirty);

  // ✕ / Cancel / Esc / backdrop: ask first when ticks have changed.
  const close = () => { void confirmDiscardIfUnsaved().then((leave) => { if (leave) onClose(); }); };

  const toggle = (id: string) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const save = () => {
    if (!dirty) { onClose(); return; }   // nothing changed — OK just closes
    void runSave({
      formName: FORM_NAME,
      auditAction: 'edit',
      save: async () => {
        await api.post(`/api/v1/inventory-items/test-method/${testMethodOid}/assign`,
          { selectedItemOids: [...selected] });
      },
      onSaved,
      onError: (e) => {
        const msg = (e as { response?: { data?: { error?: { message?: string } } } })
          .response?.data?.error?.message;
        notify.error(msg ?? 'Could not save the item assignments.');
      },
    });
  };

  const title = [testCode, testName].filter((s) => s.trim() !== '').join(' · ');

  return (
    <AppDialog
      open
      title={`Assign Items — ${title}`}
      width={860}
      className="dlg-center inv-24"
      maximizable
      onClose={close}
      footer={
        <>
          <button type="button" className="ca-btn ca-btn-solid" disabled={loading} onClick={save}>OK</button>
          <button type="button" className="ca-btn" onClick={close}>Cancel</button>
        </>
      }
    >
      <div className="aid-top">
        <span className="aid-count">{selected.size} selected</span>
        {/* Enter here searches — it must not press OK (AppDialog's Enter-to-submit). */}
        <div data-hotkeys-ignore>
          <GridSearch value={search} onChange={setSearch} placeholder="Text to search…" />
        </div>
      </div>
      <AppDataGrid<AssignItemRow>
        rows={visible}
        columns={COLUMNS}
        getRowId={(r) => r.oid}
        loading={loading}
        defaultPageSize={20}
        initialSort={{ key: 'item', dir: 'asc' }}
        selectable
        selectedIds={selected}
        onSelectionChange={setSelected}
        onRowClick={(r) => toggle(r.oid)}
        emptyText="No items"
      />
    </AppDialog>
  );
}
